import gsap from "gsap";

const FLIP_DURATION = 0.5;
const TEXT_FADE_DURATION = 0.3; // reference: NOTIFY_TEXT_FADE_DURATION_S
// Reference doesn't quote this constant directly — 3s is a reasonable
// stub default for how long the thank-you screen holds before the card
// auto-dismisses back to the teaser.
const PROGRESS_DURATION = 3;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Port of the reference's TcoPhase2NotifyFlip.tsx — a rotateX flip card:
// teaser front ("Notify me") / email-input back, with a thank-you overlay
// and an auto-dismiss progress bar on success. This project has no
// CMS/backend to submit to (the reference posts to a real endpoint via
// useCmsDynamicForm/useServerAction) — submission here is a UI-only stub:
// validate the email looks like one, then run the exact same
// success/thank-you/auto-dismiss choreography locally, no network call.
export default class TcoPhase2NotifyFlip {
  constructor() {
    this.flipped = false;
    this.buildDom();
  }

  buildDom() {
    const root = document.createElement("div");
    root.className = "es-notify";
    root.style.opacity = "0";
    root.style.pointerEvents = "none";

    const perspective = document.createElement("div");
    perspective.className = "es-notify-perspective";

    const flipper = document.createElement("div");
    flipper.className = "es-notify-flipper";

    const teaser = document.createElement("button");
    teaser.type = "button";
    teaser.className = "es-notify-teaser";

    const icon = document.createElement("span");
    icon.className = "es-notify-icon";
    const iconImg = document.createElement("img");
    iconImg.src = "/hyundai-tco/icons/notify-envelope.svg";
    iconImg.alt = "";
    icon.appendChild(iconImg);

    const copy = document.createElement("span");
    copy.className = "es-notify-copy";
    const headline = document.createElement("span");
    headline.className = "es-notify-headline";
    headline.textContent = "Get notified";
    const subcopy = document.createElement("span");
    subcopy.className = "es-notify-subcopy";
    this.subcopyEl = subcopy;
    copy.append(headline, subcopy);

    teaser.append(icon, copy);

    const form = document.createElement("form");
    form.className = "es-notify-form";
    form.noValidate = true;

    const progressFill = document.createElement("div");
    progressFill.className = "es-notify-progress-fill";

    const emailStep = document.createElement("div");
    emailStep.className = "es-notify-email-step";

    const inputWrap = document.createElement("div");
    inputWrap.className = "es-notify-input-wrap";

    const input = document.createElement("input");
    input.type = "email";
    input.placeholder = "Enter your email";
    input.autocomplete = "off";

    const submit = document.createElement("button");
    submit.type = "submit";
    submit.className = "es-notify-submit";
    submit.setAttribute("aria-label", "Submit");
    submit.textContent = "→";

    inputWrap.append(input, submit);
    emailStep.appendChild(inputWrap);

    const thanksStep = document.createElement("div");
    thanksStep.className = "es-notify-thanks-step";
    const thanksTitle = document.createElement("p");
    thanksTitle.className = "es-notify-thanks-title";
    thanksTitle.textContent = "You're on the list";
    const thanksBody = document.createElement("p");
    thanksBody.className = "es-notify-thanks-body";
    thanksBody.textContent = "We'll let you know when this episode is live.";
    thanksStep.append(thanksTitle, thanksBody);

    form.append(progressFill, emailStep, thanksStep);

    flipper.append(teaser, form);
    perspective.appendChild(flipper);
    root.appendChild(perspective);
    document.body.appendChild(root);

    this.root = root;
    this.flipper = flipper;
    this.teaser = teaser;
    this.form = form;
    this.progressFill = progressFill;
    this.emailStep = emailStep;
    this.thanksStep = thanksStep;
    this.input = input;

    teaser.addEventListener("click", () => this.flip(true));
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      this.handleSubmit();
    });
  }

  flip(open) {
    this.flipped = open;
    gsap.to(this.flipper, {
      rotateX: open ? 180 : 0,
      duration: FLIP_DURATION,
      ease: "power2.inOut",
    });
    if (open) this.input.focus();
  }

  handleSubmit() {
    const email = this.input.value.trim();
    if (!EMAIL_RE.test(email)) {
      this.input.classList.add("is-invalid");
      return;
    }
    this.input.classList.remove("is-invalid");
    this.showThankYou();
  }

  // UI-only success flow — no network call (this project has no
  // CMS/backend to submit to). Crossfades to the thank-you copy, runs the
  // progress-bar width tween, then auto-dismisses back to the teaser.
  showThankYou() {
    const tl = gsap.timeline({
      onComplete: () => {
        this.progressFill.style.width = "0%";
        this.emailStep.style.opacity = "1";
        this.thanksStep.style.opacity = "0";
        this.input.value = "";
        this.flip(false);
      },
    });

    tl.to(this.emailStep, { opacity: 0, duration: TEXT_FADE_DURATION, ease: "power1.inOut" }, 0);
    tl.to(this.thanksStep, { opacity: 1, duration: TEXT_FADE_DURATION, ease: "power1.inOut" }, 0);
    tl.to(this.progressFill, { width: "100%", duration: PROGRESS_DURATION, ease: "none" });
  }

  show(streamingLabel) {
    this.subcopyEl.textContent = streamingLabel || "";
    gsap.killTweensOf(this.root);
    gsap.to(this.root, { opacity: 1, duration: 0.4, ease: "power2.inOut" });
    this.root.style.pointerEvents = "auto";
  }

  hide() {
    gsap.killTweensOf(this.root);
    gsap.to(this.root, { opacity: 0, duration: 0.4, ease: "power2.inOut" });
    this.root.style.pointerEvents = "none";
    if (this.flipped) this.flip(false);
  }
}
